import { createSlice } from '@reduxjs/toolkit';

/**
 * @constant
 * @description Initial state for the authentication slice.
 * Retrieves stored authentication data from sessionStorage on app initialization.
 *
 * @type {Object}
 * @property {boolean} isLoggedIn - Whether user is currently logged in
 * @property {string|null} accessToken - JWT access token for API authentication
 * @property {Object|null} adminData - Complete admin user data object
 * @property {Array|null} permissions - User permissions array
 */
const initState = {
    isLoggedIn: sessionStorage.getItem('isLoggedIn')
        ? sessionStorage.getItem('isLoggedIn') === 'true'
        : false,
    accessToken: sessionStorage.getItem('accessToken')
        ? sessionStorage.getItem('accessToken')
        : null,
    adminData: sessionStorage.getItem('adminData')
        ? JSON.parse(sessionStorage.getItem('adminData'))
        : null,
    permissions: null
};

/**
 * @constant
 * @description Redux Toolkit slice for authentication state management.
 * Handles user login, logout, and profile updates with automatic sessionStorage persistence.
 *
 * @type {Object}
 * @property {string} name - Slice name identifier
 * @property {Object} initialState - Initial state object
 * @property {Object} reducers - Reducer functions for state mutations
 * @property {Function} reducers.loginUser - Handles user login action
 * @property {Function} reducers.logoutUser - Handles user logout action
 * @property {Function} reducers.updateUser - Handles user profile updates
 *
 * @example
 * // Dispatch login action
 * dispatch(loginUser({
 *   accessToken: 'jwt-token',
 *   name: 'John Doe',
 *   permissions: [...]
 * }));
 *
 * // Dispatch logout action
 * dispatch(logoutUser());
 */
const slice = createSlice({
    name: 'Auth',
    initialState: { ...initState },

    reducers: {
        /**
         * @function
         * @description Handles user login by updating state and persisting data to sessionStorage.
         *
         * @param {Object} auth - Current auth state
         * @param {Object} action - Redux action object
         * @param {Object} action.payload - Login payload containing user data and token
         * @param {string} action.payload.accessToken - JWT access token
         * @param {Array} action.payload.permissions - User permissions array
         *
         * @example
         * loginUser({
         *   accessToken: 'eyJhbGciOiJIUzI1NiIs...',
         *   name: 'Admin User',
         *   permissions: [{moduleName: 'users', canView: true}]
         * });
         */
        loginUser: (auth, action) => {
            sessionStorage.setItem('isLoggedIn', true);
            sessionStorage.setItem('accessToken', action.payload.accessToken);
            sessionStorage.setItem('adminData', JSON.stringify(action.payload));

            auth.isLoggedIn = true;
            auth.adminData = action.payload;
            auth.permissions = action.payload.permissions;
            auth.accessToken = action.payload.accessToken;
        },

        /**
         * @function
         * @description Handles user logout by clearing state and removing sessionStorage data.
         *
         * @param {Object} auth - Current auth state
         *
         * @example
         * dispatch(logoutUser());
         */
        logoutUser: (auth) => {
            sessionStorage.removeItem('isLoggedIn');
            sessionStorage.removeItem('accessToken');
            sessionStorage.removeItem('adminData');
            auth.isLoggedIn = false;
            auth.accessToken = null;
            auth.adminData = null;
        },

        /**
         * @function
         * @description Updates user profile data while preserving existing data.
         *
         * @param {Object} data - Current auth state
         * @param {Object} action - Redux action object
         * @param {Object} action.payload - Updated user data
         * @param {Array} action.payload.permissions - Updated permissions array
         *
         * @example
         * dispatch(updateUser({
         *   name: 'Updated Name',
         *   permissions: [...]
         * }));
         */
        updateUser: (data, action) => {
            data.adminData = {
                ...data.adminData,
                ...action.payload
            };
            data.permissions = action.payload.permissions;
        }
    }
});

/**
 * @constant
 * @description Exported action creators from the Auth slice.
 *
 * @type {Object}
 * @property {Function} loginUser - Action creator for user login
 * @property {Function} logoutUser - Action creator for user logout
 * @property {Function} updateUser - Action creator for user updates
 */
export const { loginUser, logoutUser, updateUser } = slice.actions;

/**
 * @constant
 * @description Auth slice reducer function.
 *
 * @type {Function}
 * @default slice.reducer
 */
export default slice.reducer;
